import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { PlusCircle, Pencil, Trash2 } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AddPracticeForm } from '@/components/home/AddPracticeForm';
import { EditOrganizationForm } from '@/components/home/EditOrganizationForm';
import { EditPracticeForm } from '@/components/home/EditPracticeForm';
import { DeleteConfirmationDialog } from '@/components/home/DeleteConfirmationDialog';
import { TeamAccessSheet } from '@/components/home/TeamAccessSheet';
import { Users } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

// Define interfaces to prevent 'any' type
interface Practice {
  id: number;
  tin: string;
  tin_name: string;
  practice_alias?: string;
}

interface Client {
  id: number;
  client_alias: string;
  account_id?: string;
  user_role?: string;
  practices: Practice[];
}

export default function ManageOrganization() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { clients, token, setClients, loading: authLoading, user } = useAuth(); // Add token and setClients
  const { toast } = useToast();
  const [organization, setOrganization] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAddPracticeModalOpen, setAddPracticeModalOpen] = useState(false);
  const [isEditOrgModalOpen, setEditOrgModalOpen] = useState(false);
  const [editingPractice, setEditingPractice] = useState<Practice | null>(null);
  const [deletingItem, setDeletingItem] = useState<{ id: number; name: string; type: 'organization' | 'practice' } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [teamSheetOpen, setTeamSheetOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading) {
      const org = clients.find(c => c.id.toString() === organizationId);
      if (org) {
        setOrganization(org);
      }
      setLoading(false);
    }
  }, [clients, authLoading, organizationId]);

  const handleAddPractice = async (data: { tin: string; tinName: string; practiceAlias?: string }) => {
    if (!organization) return;
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${apiUrl}/api/clients/${organization.id}/practices`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to add practice.');
    }

    // Update the local state to show the new practice immediately
    const newPractice = result.practice;
    const updatedOrganization = {
      ...organization,
      practices: [...organization.practices, newPractice],
    };
    setOrganization(updatedOrganization);

    // Update the global context as well
    setClients(clients.map(c => c.id === organization.id ? updatedOrganization : c));
    
    toast({
      title: "Success!",
      description: `The practice "${newPractice.tin_name}" has been added.`,
    });
  };

  const handleEditOrganization = async (data: { clientAlias: string; accountId?: string }) => {
    if (!organization) return;
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${apiUrl}/api/clients/${organization.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    const result = await response.json();
    if (!response.ok) { throw new Error(result.error || 'Failed to update organization.'); }
    
    const updatedOrg = { ...organization, ...result.client };
    setOrganization(updatedOrg);
    setClients(clients.map(c => c.id === organization.id ? updatedOrg : c));
    toast({ title: "Success!", description: "Organization details have been updated." });
  };

  const handleEditPractice = async (data: { tinName: string; practiceAlias?: string }) => {
    if (!editingPractice || !organization) return;
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${apiUrl}/api/clients/practices/${editingPractice.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    const result = await response.json();
    if (!response.ok) { throw new Error(result.error || 'Failed to update practice.'); }

    const updatedPractices = organization.practices.map(p => p.id === editingPractice.id ? result.practice : p);
    const updatedOrg = { ...organization, practices: updatedPractices };
    setOrganization(updatedOrg);
    setClients(clients.map(c => c.id === organization.id ? updatedOrg : c));
    toast({ title: "Success!", description: "Practice details have been updated." });
  };

  const handleDelete = async () => {
    if (!deletingItem || !organization) return;
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    setDeleteLoading(true);

    try {
      let url = '';
      if (deletingItem.type === 'organization') {
        url = `${apiUrl}/api/clients/${deletingItem.id}`;
      } else {
        url = `${apiUrl}/api/clients/practices/${deletingItem.id}`;
      }

      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || `Failed to delete ${deletingItem.type}.`);
      }
      
      toast({ title: "Success!", description: `${deletingItem.name} has been deleted.` });

      if (deletingItem.type === 'organization') {
        setClients(clients.filter(c => c.id !== deletingItem.id));
        navigate('/'); // Redirect to home after deleting the whole org
      } else {
        const updatedPractices = organization.practices.filter(p => p.id !== deletingItem.id);
        const updatedOrg = { ...organization, practices: updatedPractices };
        setOrganization(updatedOrg);
        setClients(clients.map(c => c.id === organization.id ? updatedOrg : c));
      }
      
      setDeletingItem(null); // Close dialog
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: error instanceof Error ? error.message : "An unknown error occurred." });
    } finally {
      setDeleteLoading(false);
    }
  };


  if (loading || authLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="text-center">
        <h1 className="text-2xl font-bold">Organization Not Found</h1>
        <p className="mt-2 text-muted-foreground">
          The organization you are looking for could not be found or you do not have permission to view it.
        </p>
        <Button asChild className="mt-4">
          <Link to="/">Return to Home</Link>
        </Button>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Manage {organization.client_alias} | VBCA</title>
      </Helmet>
      <div className="max-w-4xl mx-auto">
        <header className="mb-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold">Manage Organization</h1>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-2xl text-muted-foreground">{organization.client_alias}</span>
                <Button variant="ghost" size="icon" onClick={() => setEditOrgModalOpen(true)}>
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="flex gap-3 items-center">
              {/* Convenience entry — the primary Team & Access path is the Home top bar */}
              {(organization.user_role === 'owner' || organization.user_role === 'manager' || user?.role === 'admin') && (
                <Button variant="outline" onClick={() => setTeamSheetOpen(true)}>
                  <Users className="mr-2 h-4 w-4" />
                  Team &amp; Access
                </Button>
              )}
              <Button onClick={() => setAddPracticeModalOpen(true)}>
                <PlusCircle className="mr-2 h-4 w-4" />
                Add New Practice
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => setDeletingItem({ id: organization.id, name: organization.client_alias, type: 'organization' })}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Organization
              </Button>
            </div>
          </div>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Practices</CardTitle>
            <CardDescription>
              The following practices are registered under this organization.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Practice Name</TableHead>
                    <TableHead>TIN</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {organization.practices.length > 0 ? (
                    organization.practices.map(practice => (
                      <TableRow key={practice.id}>
                        <TableCell className="font-medium">
                          {practice.practice_alias || practice.tin_name}
                          {practice.practice_alias && (
                            <span className="block text-sm text-muted-foreground">{practice.tin_name}</span>
                          )}
                        </TableCell>
                        <TableCell>{practice.tin}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => setEditingPractice(practice)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeletingItem({ id: practice.id, name: practice.practice_alias || practice.tin_name, type: 'practice' })}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} className="h-24 text-center">
                        No practices registered yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

      </div>

      <TeamAccessSheet
        open={teamSheetOpen}
        onOpenChange={setTeamSheetOpen}
        initialClientId={organization.id}
      />

      <AddPracticeForm
        open={isAddPracticeModalOpen}
        onOpenChange={setAddPracticeModalOpen}
        onSubmit={handleAddPractice}
        organizationName={organization.client_alias}
      />

      <EditOrganizationForm
        open={isEditOrgModalOpen}
        onOpenChange={setEditOrgModalOpen}
        onSubmit={handleEditOrganization}
        organization={organization}
      />
      
      <EditPracticeForm
        open={!!editingPractice}
        onOpenChange={(isOpen) => !isOpen && setEditingPractice(null)}
        onSubmit={handleEditPractice}
        practice={editingPractice}
      />

      <DeleteConfirmationDialog
        open={!!deletingItem}
        onOpenChange={(isOpen) => !isOpen && setDeletingItem(null)}
        onConfirm={handleDelete}
        itemName={deletingItem?.name || ''}
        itemType={deletingItem?.type || 'practice'}
        loading={deleteLoading}
      />
    </>
  );
}
